import { useMemo, useState } from "react";
import { ChevronRight, Plus, Search } from "lucide-react";
import { useSearchParams } from "react-router";
import { toast } from "sonner";

import { DialogoCambiosSinGuardar } from "@/components/DialogoCambiosSinGuardar";
import { EstadoDeError } from "@/components/estados/EstadoDeError";
import { EstadoSinConexion } from "@/components/estados/EstadoSinConexion";
import { BarraDeGuardado, CabeceraDelMantenedor } from "@/components/plantillas/CabeceraDelMantenedor";
import { DrawerDeItemDelMaestro } from "@/components/plantillas/DrawerDeItemDelMaestro";
import { InsigniaDeIa } from "@/components/plantillas/GeneracionConIa";
import { ChipDeTipo, ESTILO_DE_OBLIGATORIEDAD } from "@/components/plantillas/etiquetas";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { NativeSelect } from "@/components/ui/native-select";
import { Skeleton } from "@/components/ui/skeleton";
import { useGuardiaDeCambios } from "@/hooks/useGuardiaDeCambios";
import {
  useActualizarMaestroItem,
  useCrearMaestroItem,
  useMaestroItems,
  usePlantillas,
} from "@/hooks/usePlantillas";
import { getApiErrorMessage, parseValidationErrors } from "@/lib/api";
import {
  cambiosPendientesDelMaestro,
  edicionDesdeItem,
  edicionNueva,
  gruposDelMaestro,
  siguienteUso,
  subgruposDeUnGrupo,
  type EdicionDeItemDelMaestro,
} from "@/lib/borradorDelMaestro";
import { estadoDeConsulta } from "@/lib/estadoDeConsulta";
import { TIPOS_DE_CONTENIDO } from "@/lib/plantillas";
import { cn } from "@/lib/utils";

interface FilaDelMaestro extends EdicionDeItemDelMaestro {
  nuevo: boolean;
}

function mensajeDeError(error: unknown): string {
  const errores = parseValidationErrors(error);
  if (errores) return Object.values(errores)[0];
  return getApiErrorMessage(error, "No pudimos guardar los cambios. Inténtalo de nuevo.");
}

/**
 * El maestro de ítems: el catálogo que alimenta todas las plantillas.
 *
 * Como en el editor de plantillas, se trabaja sobre BORRADORES —uno por ítem
 * tocado— y se guarda todo junto. Cambiar un ítem acá vale para todas las
 * plantillas que lo usan, y eso es justamente lo que esta pantalla ofrece y la
 * del editor no: ahí solo se decide lo de UNA plantilla.
 */
export function MaestroDeItems() {
  const maestro = useMaestroItems();
  const plantillas = usePlantillas();
  const crear = useCrearMaestroItem();
  const actualizar = useActualizarMaestroItem();
  const estado = estadoDeConsulta(maestro);

  const [parametros] = useSearchParams();
  const [abierto, setAbierto] = useState<string | null>(parametros.get("item"));
  const [borradores, setBorradores] = useState<Record<string, EdicionDeItemDelMaestro>>({});
  const [guardando, setGuardando] = useState(false);
  const [errorDeGuardado, setErrorDeGuardado] = useState<string | null>(null);

  const [busqueda, setBusqueda] = useState("");
  const [grupoFiltro, setGrupoFiltro] = useState("todos");
  const [tipoFiltro, setTipoFiltro] = useState("todos");

  const items = useMemo(() => maestro.data ?? [], [maestro.data]);
  const listaDePlantillas = useMemo(() => plantillas.data ?? [], [plantillas.data]);
  const idsDePlantillas = useMemo(() => listaDePlantillas.map((p) => p.id), [listaDePlantillas]);

  const pendientes = useMemo(
    () => cambiosPendientesDelMaestro(items, borradores, idsDePlantillas),
    [items, borradores, idsDePlantillas],
  );
  const hayCambios = pendientes.length > 0;
  const guardia = useGuardiaDeCambios(hayCambios);

  /* --- La lista que se dibuja: originales con su borrador encima ----------- */

  const filasBase: FilaDelMaestro[] = useMemo(() => {
    const lista: FilaDelMaestro[] = items.map((item) => ({
      ...(borradores[String(item.id)] ?? edicionDesdeItem(item, idsDePlantillas)),
      nuevo: false,
    }));
    for (const [clave, borrador] of Object.entries(borradores)) {
      if (items.some((i) => String(i.id) === clave)) continue;
      let ultimo = -1;
      lista.forEach((f, k) => {
        if (f.grupo === borrador.grupo) ultimo = k;
      });
      lista.splice(ultimo < 0 ? lista.length : ultimo + 1, 0, { ...borrador, nuevo: true });
    }
    return lista;
  }, [items, borradores, idsDePlantillas]);

  const grupos = useMemo(() => gruposDelMaestro(items), [items]);
  const tipos = useMemo(
    () =>
      TIPOS_DE_CONTENIDO.filter((t) => items.some((i) => i.tipo_contenido === t.valor)),
    [items],
  );
  // De los ítems GUARDADOS salen las dependencias: uno nuevo todavía no tiene id.
  const candidatos = useMemo(() => items.map((i) => ({ id: i.id, nombre: i.nombre })), [items]);
  const fuentes = useMemo(
    () => [...new Set(items.map((i) => i.fuente).filter((f): f is string => Boolean(f)))],
    [items],
  );

  const q = busqueda.trim().toLowerCase();
  const visibles = filasBase.filter(
    (f) =>
      (grupoFiltro === "todos" || f.grupo === grupoFiltro) &&
      (tipoFiltro === "todos" || f.tipo === tipoFiltro) &&
      (!q || `${f.nombre} ${f.descripcion} ${f.fuente}`.toLowerCase().includes(q)),
  );

  const secciones = useMemo(() => {
    const mapa = new Map<string, { grupo: string; subgrupo: string; filas: FilaDelMaestro[] }>();
    for (const fila of visibles) {
      const clave = `${fila.grupo}|${fila.subgrupo}`;
      const existente = mapa.get(clave);
      if (existente) existente.filas.push(fila);
      else mapa.set(clave, { grupo: fila.grupo, subgrupo: fila.subgrupo, filas: [fila] });
    }
    return [...mapa.values()];
  }, [visibles]);

  /* --- Edición --------------------------------------------------------------- */

  const edicionActual = abierto
    ? (filasBase.find((f) => f.clave === abierto) ?? null)
    : null;
  const original = abierto ? items.find((i) => String(i.id) === abierto) : undefined;

  /* Los subgrupos del grupo elegido; si el ítem trae uno que el maestro todavía no
     tiene (un ítem nuevo), se ofrece igual para que el selector no mienta. */
  const subgruposParaElegir = useMemo(() => {
    if (!edicionActual) return [];
    const lista = subgruposDeUnGrupo(items, edicionActual.grupo);
    return edicionActual.subgrupo && !lista.includes(edicionActual.subgrupo)
      ? [...lista, edicionActual.subgrupo]
      : lista;
  }, [items, edicionActual]);

  const editar = (clave: string, cambios: Partial<EdicionDeItemDelMaestro>) => {
    const base = filasBase.find((f) => f.clave === clave);
    if (!base) return;
    const { nuevo: _nuevo, ...edicion } = base;
    void _nuevo;
    setBorradores((actuales) => ({ ...actuales, [clave]: { ...edicion, ...cambios } }));
  };

  const ciclarUso = (plantillaId: number) => {
    if (!edicionActual) return;
    const clave = String(plantillaId);
    editar(edicionActual.clave, {
      uso: { ...edicionActual.uso, [clave]: siguienteUso(edicionActual.uso[clave] ?? "") },
    });
  };

  const nuevoItem = () => {
    const grupo = grupoFiltro !== "todos" ? grupoFiltro : (items[items.length - 1]?.grupo ?? "");
    const hermanos = items.filter((i) => i.grupo === grupo);
    const subgrupo = hermanos[hermanos.length - 1]?.subgrupo ?? "";
    const clave = `nuevo:${Date.now()}`;
    setBorradores((actuales) => ({
      ...actuales,
      [clave]: edicionNueva(clave, grupo, subgrupo, idsDePlantillas),
    }));
    setBusqueda("");
    setTipoFiltro("todos");
    setAbierto(clave);
    toast("Ítem creado: se agrega al maestro cuando guardes los cambios");
  };

  const descartar = () => {
    setBorradores({});
    setAbierto(null);
    setErrorDeGuardado(null);
    toast("Cambios descartados");
  };

  /**
   * Guarda ítem por ítem, en el orden en que se tocaron. Si uno falla se
   * detiene ahí: los ya guardados salen del borrador (son parte del servidor)
   * y el resto sigue siendo borrador, con el motivo a la vista.
   */
  const guardar = async (): Promise<boolean> => {
    setGuardando(true);
    setErrorDeGuardado(null);
    let guardados = 0;

    try {
      for (const cambio of pendientes) {
        if (cambio.tipo === "crear") {
          const creado = await crear.mutateAsync(cambio.datos);
          if (Object.keys(cambio.uso).length > 0) {
            await actualizar.mutateAsync({ id: creado.id, ...cambio.datos, uso: cambio.uso });
          }
          setBorradores(({ [cambio.clave]: _quitado, ...resto }) => {
            void _quitado;
            return resto;
          });
          if (abierto === cambio.clave) setAbierto(null);
        } else {
          await actualizar.mutateAsync({ id: cambio.id, ...cambio.datos });
          setBorradores(({ [String(cambio.id)]: _quitado, ...resto }) => {
            void _quitado;
            return resto;
          });
        }
        guardados++;
      }
      toast.success(guardados === 1 ? "Cambio guardado" : "Cambios guardados");
      return true;
    } catch (error) {
      setErrorDeGuardado(mensajeDeError(error));
      return false;
    } finally {
      setGuardando(false);
    }
  };

  return (
    <div className="px-6 pt-2 pb-6 lg:px-8 lg:pb-8">
      <CabeceraDelMantenedor vista="maestro">
        <BarraDeGuardado
          hayCambios={hayCambios}
          guardando={guardando}
          onDescartar={descartar}
          onGuardar={() => void guardar()}
        />
      </CabeceraDelMantenedor>

      <div className="mb-[1.125rem] flex flex-wrap items-end justify-between gap-4">
        <p className="text-ink-body max-w-[40rem] flex-1 text-sm font-medium text-pretty">
          Todos los contenidos posibles de un informe, sin repetir. Un cambio aquí se refleja en
          todas las plantillas que usan el ítem.
        </p>
        <Button
          variant="secondary"
          onClick={nuevoItem}
          disabled={estado.cargando || estado.hayError}
        >
          <Plus className="size-4" />
          Nuevo ítem
        </Button>
      </div>

      <div className="space-y-4">
      {estado.sinConexion && <EstadoSinConexion que="el maestro de ítems" />}

      {estado.hayError && (
        <EstadoDeError
          titulo="No se pudo cargar el maestro de ítems"
          error={maestro.error}
          onReintentar={maestro.refetch}
          reintentando={maestro.isFetching}
        />
      )}

      {errorDeGuardado ? (
        <Alert variant="destructive">
          <AlertDescription>{errorDeGuardado}</AlertDescription>
        </Alert>
      ) : null}

      {estado.cargando && <Skeleton className="h-96 w-full rounded-xl" />}

      {maestro.data ? (
        <>
          <div className="flex flex-wrap items-center gap-3">
            <div className="relative min-w-[12.5rem] max-w-[20rem] flex-1">
              <Search
                aria-hidden="true"
                className="text-ink-hint pointer-events-none absolute top-1/2 left-3.5 size-4 -translate-y-1/2"
              />
              <Input
                aria-label="Buscar en el maestro"
                placeholder="Buscar en el maestro…"
                value={busqueda}
                onChange={(e) => setBusqueda(e.target.value)}
                className="pl-10"
              />
            </div>
            <div className="w-56">
              <NativeSelect
                aria-label="Grupo"
                value={grupoFiltro}
                onChange={(e) => setGrupoFiltro(e.target.value)}
              >
                <option value="todos">Todos los grupos</option>
                {grupos.map((g) => (
                  <option key={g} value={g}>
                    {g}
                  </option>
                ))}
              </NativeSelect>
            </div>
            <div className="w-52">
              <NativeSelect
                aria-label="Tipo de contenido"
                value={tipoFiltro}
                onChange={(e) => setTipoFiltro(e.target.value)}
              >
                <option value="todos">Todos los tipos</option>
                {tipos.map((t) => (
                  <option key={t.valor} value={t.valor}>
                    {t.etiqueta}
                  </option>
                ))}
              </NativeSelect>
            </div>
            <span className="text-ink-hint ml-auto text-[0.78rem] font-semibold">
              <span className="text-ink font-mono">{visibles.length}</span>{" "}
              {visibles.length === 1 ? "ítem" : "ítems"}
            </span>
          </div>

          {secciones.length === 0 ? (
            <p className="text-ink-muted rounded-xl border p-6 text-center text-sm">
              Ningún ítem coincide con la búsqueda o los filtros.
            </p>
          ) : (
            <div className="bg-card overflow-hidden rounded-xl border">
              {secciones.map((seccion, i) => (
                <div key={`${seccion.grupo}|${seccion.subgrupo}`}>
                  <div
                    data-testid={`cabecera-${seccion.grupo}-${seccion.subgrupo}`}
                    className={cn(
                      "flex items-center gap-2 px-5 pt-4 pb-1.5",
                      i > 0 && "border-divisor border-t",
                    )}
                  >
                    <span className="text-ink text-[0.8125rem] font-extrabold">{seccion.grupo}</span>
                    {seccion.subgrupo ? (
                      <>
                        <ChevronRight aria-hidden="true" className="text-ink-ghost size-3" />
                        <span className="text-ink-muted text-[0.78rem] font-bold">
                          {seccion.subgrupo}
                        </span>
                      </>
                    ) : null}
                    <span className="text-ink-hint font-mono text-[0.65625rem] font-semibold">
                      {seccion.filas.length}
                    </span>
                  </div>

                  {seccion.filas.map((fila) => {
                    const usos = idsDePlantillas.map((id) => fila.uso[String(id)] ?? "");
                    const enUso = usos.filter(Boolean).length;
                    const nombres = listaDePlantillas
                      .filter((p) => fila.uso[String(p.id)])
                      .map((p) => p.nombre)
                      .join("\n");

                    return (
                      <div
                        key={fila.clave}
                        data-testid={`fila-${fila.clave}`}
                        onClick={() => setAbierto(fila.clave)}
                        className={cn(
                          "hover:bg-superficie-sutil flex cursor-pointer items-center gap-3.5 px-5 py-[0.5625rem]",
                          abierto === fila.clave && "bg-success-soft/50",
                        )}
                      >
                        <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                          <div className="flex min-w-0 items-center gap-2">
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                setAbierto(fila.clave);
                              }}
                              className="text-ink truncate text-left text-[0.90625rem] font-semibold outline-none focus-visible:underline"
                            >
                              {fila.nombre}
                            </button>
                            <InsigniaDeIa
                              modo={fila.modo}
                              tipo={fila.tipo}
                              nombre={fila.nombre}
                              dependencias={fila.dependencias.length}
                              alFinal={fila.seRedactaAlFinal}
                            />
                          </div>
                          {fila.descripcion ? (
                            <p className="text-ink-hint truncate text-xs font-medium">
                              {fila.descripcion}
                            </p>
                          ) : null}
                        </div>
                        <ChipDeTipo tipo={fila.tipo} ancho className="hidden md:inline-flex" />
                        <span className="text-ink-hint hidden w-[9.375rem] shrink-0 truncate text-xs font-medium lg:block">
                          {fila.fuente || "—"}
                        </span>
                        <span
                          title={nombres || "Sin plantillas"}
                          className="flex w-[8.75rem] shrink-0 items-center gap-2"
                        >
                          <span
                            role="img"
                            aria-label={`Usado en ${enUso} de ${idsDePlantillas.length} plantillas`}
                            className="flex items-center gap-[3px]"
                          >
                            {usos.map((uso, k) => (
                              <span
                                key={k}
                                className={cn(
                                  "size-[0.4375rem] rounded-full",
                                  uso ? ESTILO_DE_OBLIGATORIEDAD[uso].punto : "bg-border",
                                )}
                              />
                            ))}
                          </span>
                          <span className="text-ink-muted font-mono text-[0.6875rem] font-semibold whitespace-nowrap">
                            {enUso} de {idsDePlantillas.length}
                          </span>
                        </span>
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
          )}

          <DrawerDeItemDelMaestro
            edicion={edicionActual}
            frecuencia={original ? original.frecuencia : null}
            ejemplo={original ? original.ejemplo : null}
            plantillas={listaDePlantillas.map((p) => ({ id: p.id, nombre: p.nombre }))}
            grupos={
              edicionActual && !grupos.includes(edicionActual.grupo)
                ? [...grupos, edicionActual.grupo]
                : grupos
            }
            subgrupos={subgruposParaElegir}
            fuentesConocidas={fuentes}
            idDelItem={original ? original.id : null}
            candidatos={candidatos}
            onCambiar={(cambios) => abierto && editar(abierto, cambios)}
            onCiclarUso={ciclarUso}
            onCerrar={() => setAbierto(null)}
          />
        </>
      ) : null}

      </div>

      <DialogoCambiosSinGuardar
        abierto={guardia.bloqueado}
        descripcion="El maestro tiene cambios que no se han guardado. Si sales ahora se perderán y los ítems quedarán como en el último guardado."
        guardando={guardando}
        onGuardarYSalir={async () => {
          if (await guardar()) {
            guardia.desactivar();
            guardia.continuar();
          } else {
            guardia.cancelar();
          }
        }}
        onSalirSinGuardar={guardia.continuar}
        onCancelar={guardia.cancelar}
      />
    </div>
  );
}
